import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getMessages, sendMessage, updateMessage, analyzeMessages, markMessagesRead,
  getTaggedMessages, uploadVoiceMessage,
} from '../api/client';

// Shared conversation view — used both inside a Client's profile (Messages tab)
// and on the global Messages page. Mounting/changing `clientId` is treated as
// "opening" that conversation (marks it read), matching the old tab-open behavior
// this was extracted from.
export default function MessageThread({ clientId, client, onMessageSent }) {
  const { profile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState('');
  const [analyzingMessages, setAnalyzingMessages] = useState(false);
  const [messageAnalysis, setMessageAnalysis] = useState(null);
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [taggedMessages, setTaggedMessages] = useState([]);
  const [loadingTagged, setLoadingTagged] = useState(false);
  const [coachRecording, setCoachRecording] = useState(false);
  const [coachRecordingTime, setCoachRecordingTime] = useState(0);
  const [coachAudioBlob, setCoachAudioBlob] = useState(null);
  const [coachAudioUrl, setCoachAudioUrl] = useState('');
  const coachMediaRecorderRef = useRef(null);
  const coachChunksRef = useRef([]);
  const coachTimerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    setMessages([]);
    setMessageAnalysis(null);
    setActiveTagFilter(null);
    setTaggedMessages([]);
    setNewMessage('');
    if (!clientId) return;

    getMessages(clientId).then(res => setMessages(res.data || [])).catch(() => setError('Could not load messages.'));
    markMessagesRead(clientId).catch(() => {});

    const interval = setInterval(() => {
      getMessages(clientId).then(res => setMessages(res.data || [])).catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [clientId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const initials = client?.name ? client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '';

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const res = await sendMessage(clientId, { client_id: clientId, message_type: 'text', content: newMessage.trim() });
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
      onMessageSent?.();
    } catch (err) {
      setError('Could not send message.');
    } finally {
      setSendingMessage(false);
    }
  };

  const startCoachRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      coachChunksRef.current = [];
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'].find(t => MediaRecorder.isTypeSupported(t)) || 'audio/webm';
      coachMediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      coachMediaRecorderRef.current.ondataavailable = e => { if (e.data.size > 0) coachChunksRef.current.push(e.data); };
      coachMediaRecorderRef.current.onstop = () => {
        const blob = new Blob(coachChunksRef.current, { type: mimeType });
        setCoachAudioBlob(blob);
        setCoachAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      coachMediaRecorderRef.current.start(1000);
      setCoachRecordingTime(0);
      coachTimerRef.current = setInterval(() => setCoachRecordingTime(t => t + 1), 1000);
      setCoachRecording(true);
    } catch (err) {
      setError('Microphone access denied.');
    }
  };

  const stopCoachRecording = () => {
    if (coachMediaRecorderRef.current) coachMediaRecorderRef.current.stop();
    if (coachTimerRef.current) clearInterval(coachTimerRef.current);
    setCoachRecording(false);
  };

  const handleSendVoiceMessage = async () => {
    if (!coachAudioBlob) return;
    setSendingMessage(true);
    try {
      const formData = new FormData();
      formData.append('file', coachAudioBlob, `voice_${Date.now()}.webm`);
      const uploadRes = await uploadVoiceMessage(clientId, formData);
      await sendMessage(clientId, { client_id: clientId, message_type: 'voice', audio_url: uploadRes.data.url, duration_seconds: coachRecordingTime, transcription: uploadRes.data.transcription || null });
      setCoachAudioBlob(null);
      setCoachAudioUrl('');
      setCoachRecordingTime(0);
      const res = await getMessages(clientId);
      setMessages(res.data || []);
      onMessageSent?.();
    } catch (err) {
      setError('Could not send voice message.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAnalyzeMessages = async () => {
    setAnalyzingMessages(true);
    try {
      const res = await analyzeMessages(clientId);
      setMessageAnalysis(res.data.analysis);
    } catch (err) {
      setError('Could not analyze messages.');
    } finally {
      setAnalyzingMessages(false);
    }
  };

  const toggleMessageAnalysis = async (messageId, include) => {
    try {
      await updateMessage(messageId, { include_in_analysis: include });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, include_in_analysis: include } : m));
    } catch (err) {
      setError('Could not update message.');
    }
  };

  const toggleMessageTag = async (messageId, tag) => {
    const msg = messages.find(m => m.id === messageId);
    if (!msg) return;
    const currentTags = msg.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    try {
      await updateMessage(messageId, { tags: newTags });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, tags: newTags } : m));
      if (activeTagFilter) {
        loadTaggedMessages(activeTagFilter);
      }
    } catch (err) {
      setError('Could not update tag.');
    }
  };

  const loadTaggedMessages = async (tag) => {
    setLoadingTagged(true);
    setActiveTagFilter(tag);
    try {
      const res = await getTaggedMessages(clientId, tag);
      setTaggedMessages(res.data || []);
    } catch (err) {
      setError('Could not load tagged messages.');
    } finally {
      setLoadingTagged(false);
    }
  };

  const clearTagFilter = () => {
    setActiveTagFilter(null);
    setTaggedMessages([]);
  };

  if (!clientId) return null;

  return (
    <div style={styles.card}>
      {error && (
        <div style={{ padding: '12px 22px', background: 'var(--red-light)', color: 'var(--red)', fontSize: '13px' }}>
          {error}
        </div>
      )}
      <div style={styles.cardHeader}>
        <span style={styles.cardTitle}>Conversation</span>
        {messages.length > 0 && (
          <button style={styles.cardAction} onClick={handleAnalyzeMessages} disabled={analyzingMessages}>
            {analyzingMessages ? 'Analyzing...' : '◎ Analyze'}
          </button>
        )}
      </div>
      <div style={styles.cardBody}>

        {/* Analysis */}
        {messageAnalysis && (
          <div style={styles.analysisCard}>
            <div style={styles.analysisSectionTitle}>Between-session insights</div>
            <div style={styles.analysisText}>{messageAnalysis.summary}</div>
            {messageAnalysis.themes?.length > 0 && (
              <div style={{ ...styles.tagRow, marginTop: '10px' }}>
                {messageAnalysis.themes.map((t, i) => <span key={i} style={styles.themeTag}>{t}</span>)}
              </div>
            )}
            {messageAnalysis.coachingNotes && (
              <div style={{ marginTop: '12px' }}>
                <div style={styles.analysisLabel}>Coaching notes</div>
                <div style={styles.analysisText}>{messageAnalysis.coachingNotes}</div>
              </div>
            )}
            {messageAnalysis.sessionRelevance && (
              <div style={{ marginTop: '12px' }}>
                <div style={styles.analysisLabel}>For next session</div>
                <div style={styles.analysisText}>{messageAnalysis.sessionRelevance}</div>
              </div>
            )}
          </div>
        )}

        {/* Tag filter pills */}
        {messages.length > 0 && (
          <div style={styles.tagFilterRow}>
            <div style={styles.tagFilterLabel}>Filter:</div>
            {[
              { id: 'pain_point', label: '⚡ Pain points', color: '#C25E5E', bg: '#F5EAEA' },
              { id: 'breakthrough', label: '✦ Breakthroughs', color: '#C9A84C', bg: '#FAF3E0' },
              { id: 'testimonial', label: '★ Testimonials', color: 'var(--sage)', bg: 'var(--mint)' },
            ].map(tag => (
              <button
                key={tag.id}
                style={{
                  ...styles.tagFilterPill,
                  background: activeTagFilter === tag.id ? tag.color : tag.bg,
                  color: activeTagFilter === tag.id ? 'white' : tag.color,
                  border: `1px solid ${tag.color}`,
                }}
                onClick={() => activeTagFilter === tag.id ? clearTagFilter() : loadTaggedMessages(tag.id)}
              >
                {tag.label}{activeTagFilter === tag.id && ' ×'}
              </button>
            ))}
          </div>
        )}

        {/* Tagged messages view */}
        {activeTagFilter && (
          <div style={styles.taggedView}>
            <div style={styles.taggedViewTitle}>
              {loadingTagged ? 'Loading...' : `${taggedMessages.length} message${taggedMessages.length !== 1 ? 's' : ''} tagged`}
            </div>
            {!loadingTagged && taggedMessages.length === 0 && (
              <div style={styles.taggedEmpty}>No messages tagged yet. Tag messages below using the tag buttons.</div>
            )}
            {taggedMessages.map(msg => (
              <div key={msg.id} style={styles.taggedMessage}>
                <div style={styles.taggedMessageMeta}>
                  <span style={styles.messageSender}>{msg.sender_type === 'coach' ? 'You' : client?.name}</span>
                  <span style={styles.messageTime}>
                    {new Date(msg.created_at).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                {msg.message_type === 'text' && (
                  <div style={styles.taggedMessageContent}>{msg.content}</div>
                )}
                {msg.message_type === 'voice' && (
                  <div style={styles.taggedMessageContent}>
                    <div style={{ fontSize: '11px', color: 'var(--text-soft)', marginBottom: '6px' }}>🎙 Voice message</div>
                    <audio controls src={msg.audio_url} style={{ width: '100%', borderRadius: '6px' }} />
                    {msg.transcription && <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '6px', fontStyle: 'italic' }}>{msg.transcription}</div>}
                  </div>
                )}
              </div>
            ))}
            <div style={styles.divider} />
          </div>
        )}

        {/* Messages list */}
        {messages.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>💬</div>
            <p style={styles.emptyText}>No messages yet. Send the first message below.</p>
          </div>
        ) : (
          <div style={styles.messagesList}>
            {messages.map(msg => (
              <div key={msg.id} style={{ ...styles.messageRow, ...(msg.sender_type === 'coach' ? styles.messageRowCoach : styles.messageRowClient) }}>
                {msg.sender_type === 'client' && (
                  <div style={styles.messageAvatar}>{initials}</div>
                )}
                <div style={{ maxWidth: '75%' }}>
                  <div style={styles.messageMeta}>
                    <span style={styles.messageSender}>{msg.sender_type === 'coach' ? 'You' : client?.name}</span>
                    <span style={styles.messageTime}>
                      {new Date(msg.created_at).toLocaleDateString('default', { month: 'short', day: 'numeric' })} · {new Date(msg.created_at).toLocaleTimeString('default', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                    <button
                      style={{ ...styles.includeBtn, ...(msg.include_in_analysis ? styles.includeBtnActive : {}) }}
                      onClick={() => toggleMessageAnalysis(msg.id, !msg.include_in_analysis)}
                    >
                      {msg.include_in_analysis ? '◎' : '○'}
                    </button>
                  </div>
                  {msg.message_type === 'text' && (
                    <div style={{ ...styles.messageBubble, ...(msg.sender_type === 'coach' ? styles.messageBubbleCoach : styles.messageBubbleClient) }}>
                      {msg.content}
                    </div>
                  )}
                  {msg.message_type === 'voice' && (
                    <div style={{ ...styles.messageBubble, ...(msg.sender_type === 'coach' ? styles.messageBubbleCoach : styles.messageBubbleClient), padding: '10px 14px', minWidth: '280px' }}>
                      <div style={{ fontSize: '11px', color: msg.sender_type === 'coach' ? 'rgba(255,255,255,0.7)' : 'var(--text-soft)', marginBottom: '6px' }}>🎙 Voice message</div>
                      <audio controls src={msg.audio_url} style={{ width: '100%', borderRadius: '6px' }} />
                      {msg.duration_seconds && <div style={{ fontSize: '11px', color: msg.sender_type === 'coach' ? 'rgba(255,255,255,0.6)' : 'var(--text-faint)', marginTop: '4px' }}>{Math.floor(msg.duration_seconds / 60)}:{String(msg.duration_seconds % 60).padStart(2, '0')}</div>}
                      {msg.transcription && (
                        <div style={{ fontSize: '12.5px', color: msg.sender_type === 'coach' ? 'rgba(255,255,255,0.8)' : 'var(--text-mid)', marginTop: '8px', fontStyle: 'italic', lineHeight: 1.5, borderTop: `1px solid ${msg.sender_type === 'coach' ? 'rgba(255,255,255,0.15)' : 'var(--border-light)'}`, paddingTop: '8px' }}>
                          "{msg.transcription}"
                        </div>
                      )}
                    </div>
                  )}
                  {/* Tag buttons */}
                  <div style={styles.msgTagRow}>
                    {[
                      { id: 'pain_point', label: '⚡', title: 'Pain point', color: '#C25E5E', bg: '#F5EAEA' },
                      { id: 'breakthrough', label: '✦', title: 'Breakthrough', color: '#C9A84C', bg: '#FAF3E0' },
                      { id: 'testimonial', label: '★', title: 'Testimonial', color: 'var(--sage)', bg: 'var(--mint)' },
                    ].map(tag => {
                      const tagged = (msg.tags || []).includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          style={{
                            ...styles.msgTagBtn,
                            background: tagged ? tag.bg : 'none',
                            color: tagged ? tag.color : 'var(--text-faint)',
                            border: tagged ? `1px solid ${tag.color}` : '1px solid transparent',
                          }}
                          onClick={() => toggleMessageTag(msg.id, tag.id)}
                          title={tagged ? `Remove ${tag.title} tag` : `Tag as ${tag.title}`}
                        >
                          {tag.label} {tag.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {msg.sender_type === 'coach' && (
                  <div style={{ ...styles.messageAvatar, background: 'var(--forest)', color: 'var(--gold-light)' }}>
                    {profile?.practice_name?.[0] || 'C'}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Input */}
        <div style={styles.messageInputArea}>
          {coachAudioUrl ? (
            <div style={styles.voicePreview}>
              <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginBottom: '8px' }}>🎙 Voice message ready</div>
              <audio controls src={coachAudioUrl} style={{ width: '100%', borderRadius: '6px', marginBottom: '10px' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={styles.saveBtn} onClick={handleSendVoiceMessage} disabled={sendingMessage}>{sendingMessage ? 'Sending...' : 'Send voice message'}</button>
                <button style={styles.cancelBtn} onClick={() => { setCoachAudioBlob(null); setCoachAudioUrl(''); setCoachRecordingTime(0); }}>Cancel</button>
              </div>
            </div>
          ) : coachRecording ? (
            <div style={styles.recordingBar}>
              <div style={styles.recordingDot} />
              <div style={{ fontFamily: 'monospace', fontSize: '16px', color: 'var(--red)', flex: 1 }}>
                {Math.floor(coachRecordingTime / 60)}:{String(coachRecordingTime % 60).padStart(2, '0')}
              </div>
              <button style={styles.stopRecordBtn} onClick={stopCoachRecording}>Stop recording</button>
            </div>
          ) : (
            <div style={styles.messageInputRow}>
              <textarea
                style={styles.messageInput}
                placeholder={`Message ${client?.name || ''}...`}
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                rows={2}
              />
              <div style={styles.messageInputActions}>
                <button style={styles.voiceBtn} onClick={startCoachRecording} title="Voice note">🎙</button>
                <button style={{ ...styles.saveBtn, opacity: !newMessage.trim() ? 0.5 : 1 }} onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()}>
                  {sendingMessage ? '...' : 'Send'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  card: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)', marginBottom: '20px' },
  cardHeader: { padding: '15px 22px', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle: { fontSize: '13.5px', fontWeight: '600', color: 'var(--text-dark)' },
  cardAction: { fontSize: '12.5px', color: 'var(--sage)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500' },
  cardBody: { padding: '16px 22px' },
  themeTag: { background: 'var(--mint)', color: 'var(--sage)', padding: '3px 10px', borderRadius: '20px', fontSize: '11px' },
  tagRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  divider: { borderTop: '1px solid var(--border-light)', margin: '16px 0' },
  saveBtn: { padding: '10px 18px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },
  cancelBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
  messagesList: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px', maxHeight: '500px', overflowY: 'auto', padding: '8px 0' },
  messageRow: { display: 'flex', alignItems: 'flex-end', gap: '8px' },
  messageRowCoach: { flexDirection: 'row-reverse' },
  messageRowClient: { flexDirection: 'row' },
  messageAvatar: { width: '28px', height: '28px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
  messageMeta: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px', flexWrap: 'wrap' },
  messageSender: { fontSize: '11px', fontWeight: '600', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  messageTime: { fontSize: '11px', color: 'var(--text-faint)' },
  includeBtn: { fontSize: '11px', padding: '1px 6px', borderRadius: '20px', border: '1px solid var(--border)', background: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  includeBtnActive: { borderColor: 'var(--sage)', color: 'var(--sage)', background: 'var(--mint)' },
  messageBubble: { borderRadius: '12px', padding: '10px 14px', fontSize: '14px', lineHeight: 1.6 },
  messageBubbleCoach: { background: 'var(--forest)', color: 'white', borderBottomRightRadius: '4px' },
  messageBubbleClient: { background: 'var(--mint)', color: 'var(--text-dark)', borderBottomLeftRadius: '4px' },
  messageInputArea: { borderTop: '1px solid var(--border-light)', paddingTop: '16px', marginTop: '8px' },
  messageInputRow: { display: 'flex', gap: '10px', alignItems: 'flex-end' },
  messageInput: { flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', resize: 'none' },
  messageInputActions: { display: 'flex', gap: '8px', alignItems: 'center' },
  voiceBtn: { padding: '10px 12px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '16px' },
  recordingBar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'var(--red-light)', borderRadius: '10px' },
  recordingDot: { width: '10px', height: '10px', borderRadius: '50%', background: 'var(--red)', animation: 'pulse 1s ease-in-out infinite' },
  stopRecordBtn: { padding: '8px 16px', borderRadius: '8px', background: 'var(--red)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', cursor: 'pointer' },
  voicePreview: { background: 'var(--cream)', borderRadius: '10px', padding: '14px' },
  analysisCard: { background: 'var(--mint)', borderRadius: '10px', padding: '16px', marginBottom: '20px' },
  analysisSectionTitle: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--sage)', marginBottom: '8px' },
  analysisLabel: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '4px' },
  analysisText: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.7 },
  emptyState: { textAlign: 'center', padding: '48px 24px', color: 'var(--text-soft)' },
  emptyIcon: { fontSize: '36px', marginBottom: '14px' },
  emptyText: { fontSize: '14px', color: 'var(--text-soft)', marginBottom: '16px' },
  tagFilterRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '12px 0', borderBottom: '1px solid var(--border-light)', marginBottom: '16px' },
  tagFilterLabel: { fontSize: '11.5px', fontWeight: '600', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: '4px' },
  tagFilterPill: { padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  taggedView: { marginBottom: '20px' },
  taggedViewTitle: { fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '12px' },
  taggedEmpty: { fontSize: '13.5px', color: 'var(--text-faint)', fontStyle: 'italic', padding: '12px 0' },
  taggedMessage: { background: 'var(--cream)', borderRadius: '10px', padding: '14px 16px', marginBottom: '10px', border: '1px solid var(--border-light)' },
  taggedMessageMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  taggedMessageContent: { fontSize: '14px', color: 'var(--text-dark)', lineHeight: 1.6 },
  msgTagRow: { display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' },
  msgTagBtn: { padding: '3px 8px', borderRadius: '20px', fontSize: '11px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: '500', transition: 'all 0.15s' },
};
